import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi, Spinner, ErrBanner, today, fmtDate, fmtNum } from '../../hooks/useApi';
import { api } from '../../services/api';
import './Dashboard.css';
import { useAuth } from '../../context/AuthContext';

function StatCard({ title, value, change, up, icon }) {
  return (
    <div className="stat-card">
      <div className="stat-icon">{icon}</div>
      <div className="stat-info">
        <div className="stat-title">{title}</div>
        <div className="stat-value">{value}</div>
        <div className={`stat-change ${up ? 'up' : 'down'}`}>{up ? '▲' : '▼'} {change}</div>
      </div>
    </div>
  );
}

function MiniBar({ data }) {
  const { t } = useTranslation();
  if (!data || data.length === 0) return <div style={{ padding: 16, color: 'var(--text-muted)' }}>{t('dashboard.noSalesData')}</div>;
  const max = Math.max(...data.map(d => (Number(d.diesel) || 0) + (Number(d.petrol) || 0) + (Number(d.cng) || 0)), 1);
  return (
    <div className="mini-chart">
      {data.map((d, i) => (
        <div key={i} className="bar-group">
          <div className="bars">
            <div className="bar bar-diesel" style={{ height: `${((Number(d.diesel) || 0) / max) * 80}px` }} />
            <div className="bar bar-petrol" style={{ height: `${((Number(d.petrol) || 0) / max) * 80}px` }} />
            <div className="bar bar-cng" style={{ height: `${((Number(d.cng) || 0) / max) * 80}px` }} />
          </div>
          <div className="bar-label">{d.sale_date ? d.sale_date.slice(5) : d.day}</div>
        </div>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const { t } = useTranslation();
  const { canAccess } = useAuth();
  const [date] = useState(today());
  const { data, loading, error } = useApi(() => api.dashboard.get(date), [date], null);
  const tanks = data?.tanks || [];
  const trend = data?.weekly_trend || [];
  const recent = data?.recent || [];
  if (!canAccess('dashboard')) {
    return (
      <div className="dashboard-page">
        <ErrBanner msg={t('dashboard.accessDenied')} />
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <h2 className="page-title">{t('dashboard.title')}</h2>
        <span className="date-badge">📅 {fmtDate(date)} | {t('dashboard.shift', { n: 1 })}</span>
      </div>
      {loading && <Spinner />}
      {error && <ErrBanner msg={error} />}
      {data && <>
        <div className="stats-grid">
          <StatCard title={t('dashboard.todaySales')} value={`₹${fmtNum(data.today_sales || 0)}`} change={t('dashboard.todaySalesChange')} up={true} icon="💰" />
          <StatCard title={t('dashboard.activePumps')} value={data.active_pumps ?? '—'} change={t('dashboard.activePumpsChange')} up={true} icon="⛽" />
          <StatCard title={t('dashboard.creditPending')} value={`₹${fmtNum(data.credit_pending || 0)}`} change={t('dashboard.creditPendingChange')} up={false} icon="💳" />
          <StatCard title={t('dashboard.fuelInStock')} value={`${fmtNum(tanks.reduce((s, t) => s + Number(t.current_stock || 0), 0))} L`} change={t('dashboard.fuelInStockChange')} up={true} icon="🛢️" />
        </div>
        <div className="dashboard-row">
          <div className="section-card chart-card">
            <div className="section-header">{t('dashboard.weeklyTrend')}</div>
            <div style={{ padding: 16 }}>
              <MiniBar data={trend} />
              <div className="chart-legend">
                <span className="legend-item"><span className="dot dot-diesel" />{t('dashboard.diesel')}</span>
                <span className="legend-item"><span className="dot dot-petrol" />{t('dashboard.petrol')}</span>
                <span className="legend-item"><span className="dot dot-cng" />{t('dashboard.cng')}</span>
              </div>
            </div>
          </div>
          <div className="section-card stock-card">
            <div className="section-header">{t('dashboard.tankStockLevels')}</div>
            <div className="stock-list">
              {tanks.map(t => {
                const pct = Math.round((Number(t.current_stock) / Number(t.capacity)) * 100) || 0;
                const color = t.product_code === 'MS' ? '#e53e3e' : t.product_code === 'HSD' ? '#1a6fad' : '#2d9e5f';
                return (
                  <div key={t.id} className="stock-item">
                    <div className="stock-name">{t.tank_no} ({t.product_code})</div>
                    <div className="stock-bar-wrap"><div className="stock-bar" style={{ width: `${pct}%`, background: color }} /></div>
                    <div className="stock-nums"><span>{fmtNum(t.current_stock)} L</span><span className="stock-pct">{pct}%</span></div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="section-card">
          <div className="section-header">{t('dashboard.recentTransactions')}</div>
          <div className="table-wrapper">
            <table className="data-table">
              <thead><tr>
                <th style={{ textAlign: 'left' }}>{t('dashboard.ref')}</th>
                <th style={{ textAlign: 'left' }}>{t('common.date')}</th>
                <th style={{ textAlign: 'left' }}>{t('common.type')}</th>
                <th>{t('dashboard.amountRs')}</th>
                <th style={{ textAlign: 'left' }}>{t('common.status')}</th>
              </tr></thead>
              <tbody>
                {recent.map((t, i) => (
                  <tr key={i}>
                    <td style={{ fontFamily: 'monospace', color: 'var(--primary)' }}>{t.ref || t.id}</td>
                    <td>{fmtDate(t.txn_date)}</td><td>{t.type}</td>
                    <td className="right-align">₹{fmtNum(t.amount || 0)}</td>
                    <td><span className={`badge ${t.status === 'Paid' ? 'badge-success' : t.status === 'Pending' ? 'badge-warning' : t.status === 'Received' ? 'badge-info' : 'badge-primary'}`}>{t.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>}
    </div>
  );
}
